import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import DatePicker from "@/components/DatePicker";
import { DateTime } from "luxon";
import { DEFAULT_DATE_FORMAT } from "@/constants";
import { useMemo, useCallback } from "react";
import { X } from "lucide-react";
import { noop } from "@/lib/noop";

interface DateFilterProps {
  value?: string;
  onChange: (value: string | undefined) => void;
}

export const DateFilter = ({ value, onChange }: DateFilterProps) => {
  const { t } = useTranslation();

  const dateValue = useMemo(() => {
    if (!value) return undefined;
    const dt = DateTime.fromFormat(value, DEFAULT_DATE_FORMAT);
    return dt.isValid ? dt.toJSDate() : undefined;
  }, [value]);

  const handleChange = useCallback(
    (dateString: string) => {
      onChange(dateString);
    },
    [onChange],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(undefined);
    },
    [onChange],
  );

  return (
    <div className="space-y-2">
      <Label htmlFor="date-filter">{t("contractorVisits.fields.date")}</Label>
      <div className="relative">
        <DatePicker
          value={dateValue}
          onChange={handleChange}
          onBlur={noop}
          name="date-filter"
          className="w-full h-10"
          inDialog
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100"
            aria-label={t("datePicker.clear")}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
